import { useState } from "react";
import { CreditCard, Copy, EyeOff, Loader2, Lock, Smartphone, Wallet } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useContent, pick } from "../../lib/content";
import { Reveal, Eyebrow } from "./Reveal";

const card = "h-full rounded-3xl border border-amber-900/40 bg-[#181512] p-7 transition-colors duration-500 hover:border-amber-500/50 md:p-10";

export const Trust = () => {
  const c = useContent();
  const usdt = pick(c, "usdt_address", "");
  const network = pick(c, "usdt_network", "TRC-20");
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");
  const copyUsdt = async () => {
    try { await navigator.clipboard.writeText(usdt); toast.success("Adresse USDT copiée."); } catch { /* copie refusée */ }
  };
  const pay = async () => {
    setPaying(true); setPayError("");
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "rituel_150", origin_url: window.location.origin, chat_sid: localStorage.getItem("wouffa_chat_session") });
      window.location.href = data.checkout_url;
    } catch { setPayError("Paiement momentanément indisponible. Réessayez ou passez par le chat."); setPaying(false); }
  };
  return (
  <section id="confiance" className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36" data-testid="trust-section">
    <Reveal><Eyebrow>03 — Confiance absolue</Eyebrow></Reveal>
    <Reveal delay={0.1}>
      <h2 className="mt-5 max-w-3xl font-display text-3xl leading-tight text-amber-50 sm:text-4xl lg:text-5xl">
        Aucun risque pour vous
      </h2>
    </Reveal>
    <div className="mt-14 grid gap-5 md:grid-cols-6 md:grid-rows-[auto_auto]">
      <Reveal className="md:col-span-4 md:row-span-2">
        <div className={`${card} relative flex flex-col justify-between overflow-hidden`} data-testid="trust-golden-rule">
          <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-amber-500/10 blur-3xl" />
          <p className="font-mono-x text-xs uppercase tracking-[0.25em] text-red-400">Règle d'or</p>
          <p className="mt-6 font-display text-2xl leading-snug text-amber-50 md:text-4xl">
            Si vous n'avez pas de résultat, vous ne payez pas.
          </p>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-stone-300">
            Vous ne versez absolument rien à l'avance. Le règlement de <strong className="text-amber-200">150 euros</strong> s'effectue{" "}
            <span className="font-semibold text-amber-300">uniquement après satisfaction totale</span>, lorsque vous aurez constaté
            vos résultats professionnels ou sentimentaux de vos propres yeux.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.1} className="md:col-span-2">
        <div className={card} data-testid="trust-payment">
          <Wallet className="h-6 w-6 text-amber-400" />
          <p className="mt-5 font-serif-it text-2xl italic text-amber-100">Paiement après succès</p>
          <ul className="mt-4 space-y-2 text-sm text-stone-300">
            <li className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-amber-500" /> Carte bancaire</li>
            <li className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-amber-500" /> Apple Pay &amp; Google Pay</li>
            <li className="flex items-center gap-2"><Lock className="h-4 w-4 text-amber-500" /> Link &amp; virement SEPA</li>
          </ul>
          <p className="mt-3 text-xs text-stone-400" data-testid="payment-upcoming">
            Bientôt : PayPal et Mobile Money — en cours de connexion.
          </p>
          {usdt && (
            <div className="mt-5 rounded-2xl border border-emerald-700/50 bg-emerald-900/10 p-4" data-testid="usdt-block">
              <p className="font-mono-x text-[11px] uppercase tracking-[0.25em] text-emerald-400">Payer en USDT</p>
              <div className="mt-3 flex items-center gap-4">
                <QRCodeSVG value={usdt} size={92} includeMargin={true} bgColor="#ffffff" fgColor="#0a0908"
                  className="rounded-lg bg-white p-1" data-testid="usdt-qr" />
                <div className="min-w-0 flex-1 text-left">
                  <p className="text-xs text-stone-300">Réseau <strong className="text-emerald-300">{network}</strong></p>
                  <p className="mt-1 break-all text-[11px] leading-snug text-stone-300">{usdt}</p>
                  <button onClick={copyUsdt} data-testid="usdt-copy"
                    className="mt-2 flex items-center gap-1.5 rounded-full border border-emerald-600/50 px-3 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-400 hover:text-stone-950">
                    <Copy className="h-3 w-3" /> Copier l'adresse
                  </button>
                </div>
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-stone-400">
                Envoyez le montant convenu dans le chat, puis collez la preuve d'envoi dans la discussion. Le Maître confirme dès réception.
              </p>
            </div>
          )}
          <button onClick={pay} disabled={paying} data-testid="pay-now-button"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-sm font-bold uppercase tracking-wider text-stone-950 transition-transform duration-300 hover:scale-[1.02] disabled:opacity-50">
            {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Payer les 150€ par carte
          </button>
          {payError && <p className="mt-3 text-xs text-red-400" data-testid="pay-error">{payError}</p>}
        </div>
      </Reveal>
      <Reveal delay={0.2} className="md:col-span-2">
        <div className={card} data-testid="trust-confidential">
          <EyeOff className="h-6 w-6 text-amber-400" />
          <p className="mt-5 font-serif-it text-2xl italic text-amber-100">Secret professionnel</p>
          <p className="mt-3 text-sm leading-relaxed text-stone-300">
            Vos messages, noms, photos et informations personnelles sont traités uniquement ici, en toute discrétion.
            Rien ne sortira de ce site.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
